/**
 * 宠物头像（新页面用的轻样式）：永远是 TA 自己的样子，不用名字首字（用户 2026-09-24）。
 * 取图规则和全站共用的 `PetAvatar` 是同一个 `petPortraitUrl`（shared/ui）：
 * - 有照片（主人上传的，或服务端生成的写实证件照）就用照片；
 * - 演示（fixture）模式没有照片时，用内部测试小灰猫的头像（用户授权的演示对象）；
 * - live 模式没有照片时：领养来的原创居民（origin = adopted_original）用和居民页、领养卡同一张物种插画（规则 portraitFallback 在 shared/ui，
 *   和 PetAvatar 共用），其余显示中性的爪印占位——不借演示猫冒充真实宠物，也不写首字。
 * - 照片打不开（2026-09-27 D4 第二批）：和 PetAvatar 同一条退回链（shared/ui 的 avatarChain）往下退——演示小灰猫（只在演示模式）
 *   → 物种插画（只给领养来的原创居民）→ 爪印；读屏跟着换。图不出错时画法和以前一样。
 * `petPortraitUrl`、`PawBadge`（全站唯一的“没有照片”爪印徽章，UI-BASE-01b）、`portraitFallback` 在这里转出。
 */
import { useState } from "react";
import type { PetOrigin, PetSpecies } from "@/shared/contracts";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { PawBadge, avatarChain, petPortraitUrl, portraitFallback, speciesArtLabel, useAvatarStep, type AvatarStep, type AvatarSurface } from "@/shared/ui";
import { sizedPhotoSrc, usePhotoSizes } from "@/shared/ui/photoSizes";
import { SpeciesIllustration } from "./SpeciesIllustration";
import "./pet-portrait.css";

export { PawBadge, petPortraitUrl, portraitFallback };

/**
 * origin / species：调用方手里有就直接给（到家卡片、小窝用家园快照里这只的来历与物种）。
 * 没给 origin（undefined）但给了 petId：按当前家庭（HouseholdProvider 从 /households 读的、已住进来的宠物）里这只的来历与物种来认——
 * 地图标记、“我的”等页面画的都是这个家已经住进来的宠物。给了 origin = null 表示来历不知道，不再去家庭里找。
 * 家庭里找不到这只（别人家的宠物、演示世界）就当来历不知道：爪印。
 * surface="paper"：放在昼夜都是暖纸的框里（到家卡片、小窝身份卡），底块和爪印用纸面那套固定色，写法同共享头像 PetAvatar。
 */
export function PetPortrait({ name, photoUrl, size = 40, petId, species, origin, surface }: { name: string; photoUrl?: string | null; size?: number; petId?: string; species?: PetSpecies; origin?: PetOrigin | null; surface?: AvatarSurface }) {
  const household = useOptionalCurrentHousehold();
  // 照片打不开时也要知道来历（往下退到插画还是爪印），所以给了 petId 就去家庭里认，不再只在没照片时才认。
  const brief = petId ? (household?.pets.find(({ pet }) => pet.pet_id === petId)?.pet ?? null) : null;
  const { step, photoFailed, next } = useAvatarStep(avatarChain(photoUrl, origin === undefined ? brief?.origin : origin, species ?? brief?.species));
  return <PortraitStepView step={step} name={name} photoFailed={photoFailed} size={size} surface={surface} onError={next} />;
}

/**
 * 退回链里的某一级画成 PetPortrait 的样子（2026-09-27 D4 第三批拆出来）：PetPortrait 自己用；自带一条更长退回链的地方
 * （卡包封面：证件照头像在最前；到家卡片：星球形象在最前；小窝身份卡）走完自己的前几级后，也用它画，样子和读屏与 PetPortrait 一模一样。
 * photoFailed：链里有过 TA 的照片、已经退到它后面——退到替身（演示小灰猫、爪印）时读屏说“照片暂时打不开”。
 * onError：图（照片、演示小灰猫）打不开时往下一级，由调用方的退回链决定下一级是什么。
 * 照片缩略档（UI-PET-PHOTO-SRCSET-01，10-13）：和共享头像 PetAvatar 同一套（shared/ui/photoSizes），照片那一级按 size 带一档 w；
 * 卡包、到家卡片、小窝身份卡借它画的照片也一样。没有这项能力时和原来一模一样。
 */
export function PortraitStepView({ step, name, photoFailed = false, size = 40, surface, onError }: { step: AvatarStep; name: string; photoFailed?: boolean; size?: number; surface?: AvatarSurface; onError: () => void }) {
  const paper = surface === "paper" ? " is-on-paper" : "";
  const sized = usePhotoSizes();
  // 照片（含演示小灰猫）还没到时框里先垫一枚中性爪印（pet-portrait.css 的 [data-loading]::before），图一到就撤（UI-REVIEW110-01：/me 头像圈原来先空着）
  const [loaded, setLoaded] = useState<string | null>(null);
  if (step.kind === "species") {
    // 和居民页同一张图、同一句读屏（ResidentPortrait）：说清这是物种插画，不是 TA 本人的照片。
    return (
      <span className={`ps-pet-portrait is-species${paper}`} style={{ width: size, height: size }} role="img" aria-label={speciesArtLabel(step.species, name)} data-species={step.species}>
        <SpeciesIllustration species={step.species} />
      </span>
    );
  }
  // 退到替身（演示小灰猫、爪印）时，照片其实有、只是打不开：读屏说出来，不只念名字。
  // 爪印（UI-BASE-01b，全站一套）：画 ASSET-UI-027 的徽章；本来就没有照片说“{名字}，暂无照片”，有照片但打不开说“{名字}，照片暂时打不开”（同共享头像 PetAvatar）。
  const label = photoFailed && step.kind !== "photo" ? `${name}，照片暂时打不开` : step.kind === "paw" ? `${name}，暂无照片` : name;
  if (step.kind === "paw") {
    return (
      <span className={`ps-pet-portrait is-placeholder${paper}`} style={{ width: size, height: size }} role="img" aria-label={label}>
        <PawBadge size={size} />
      </span>
    );
  }
  return (
    <span className={`ps-pet-portrait${paper}`} style={{ width: size, height: size }} role="img" aria-label={label} data-loading={loaded === step.src ? undefined : "true"}>
      <img key={step.src} src={sized ? sizedPhotoSrc(step.src, size) : step.src} alt="" loading="lazy" draggable={false} onError={onError} onLoad={() => setLoaded(step.src)} />
    </span>
  );
}
